import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9osns0senbniolesl6g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setContactForm({ name: '', email: '', message: '' });
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '发送失败，请稍后重试。');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试。');
    }
  };

  return (
    <>
      <ScrollToTop />

      {/* Schema.org AboutPage + Organization JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于073漫画网',
          description: '073漫画网品牌介绍、口碑评价与联系方式。在线免费阅读下拉式漫画，洞洞杂货铺等热门漫画尽在073漫画网。',
          url: 'https://073mh.com/about',
          isPartOf: {
            '@type': 'WebSite',
            name: '073漫画网',
            url: 'https://073mh.com',
          },
        })}
      </script>

      {/* FAQPage Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: '073漫画网的漫画是否全部免费？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '是的，073漫画网上所有漫画均完全免费阅读，无需付费订阅，也无需注册账号即可畅读。',
              },
            },
            {
              '@type': 'Question',
              name: '如何在073漫画网阅读漫画？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '073漫画网采用下拉式阅读方式，操作简单便捷。打开任意漫画章节，向下滑动即可连续阅读，无需翻页。',
              },
            },
            {
              '@type': 'Question',
              name: '073漫画网的漫画多久更新一次？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '洞洞杂货铺等热门漫画每日更新，其他漫画根据作者发布频率同步更新，确保读者第一时间看到最新内容。',
              },
            },
            {
              '@type': 'Question',
              name: '可以用手机阅读073漫画网的漫画吗？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '可以的！073漫画网完美适配手机端，无需下载APP，打开手机浏览器即可享受流畅的下拉式漫画阅读体验。',
              },
            },
          ],
        })}
      </script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

            {/* Page Header */}
            <header className="text-center mb-12">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                关于073漫画网
              </h1>
              <p className="text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                致力于为漫画爱好者提供最优质的在线免费阅读体验
              </p>
            </header>

            {/* Brand Introduction */}
            <section className="mb-14">
              <h2 className="font-heading text-xl text-foreground-900 mb-6 flex items-center gap-3">
                <span className="w-1 h-6 rounded-full bg-primary-500 inline-block"></span>
                品牌介绍
              </h2>
              <div className="bg-background-100 rounded-xl p-6 md:p-8 leading-relaxed text-foreground-700">
                <p className="mb-4">
                  <strong>073漫画网</strong>成立于2020年，是一家专注于提供高质量漫画在线免费阅读的平台。
                  我们秉持"让每一个人都能自由享受漫画的乐趣"的理念，汇聚了包括<strong>洞洞杂货铺</strong>在内的数千部热门漫画作品，
                  涵盖热血、搞笑、恋爱、悬疑、奇幻、日常、科幻、恐怖等多种类型。
                </p>
                <p className="mb-4">
                  073漫画网采用先进的<strong>下拉式阅读</strong>技术，读者只需向下滑动页面即可连续阅读漫画内容，
                  无需频繁点击翻页按钮，操作便捷流畅。同时，网站支持电脑和手机双端访问，
                  无论在何时何地，打开浏览器即可畅享阅读。
                </p>
                <p className="mb-4">
                  我们深知漫画对于每一位读者的意义——它不仅是娱乐，更是陪伴、是梦想、是心灵的慰藉。
                  因此，073漫画网始终坚持<strong>完全免费</strong>的运营模式，让每一位漫画爱好者都能无负担地追逐自己喜爱的故事。
                </p>
                <p>
                  展望未来，073漫画网将继续拓展漫画资源库，优化阅读体验，打造更加完善的社区互动功能，
                  努力成为国内最受欢迎的漫画阅读平台之一。
                </p>
              </div>

              {/* Image */}
              <div className="mt-8 rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20warm%20and%20cozy%20reading%20scene%20with%20a%20person%20reading%20manga%20on%20a%20tablet%20in%20a%20comfortable%20room%2C%20bookshelves%20in%20background%2C%20soft%20warm%20lighting%2C%20Japanese%20illustration%20style%2C%20peaceful%20atmosphere&width=1200&height=500&seq=about-brand-image&orientation=landscape"
                  alt="073漫画网 - 舒适阅读体验"
                  className="w-full h-auto"
                  width="1200"
                  height="500"
                />
              </div>
            </section>

            {/* Reputation / Testimonials */}
            <section className="mb-14">
              <h2 className="font-heading text-xl text-foreground-900 mb-6 flex items-center gap-3">
                <span className="w-1 h-6 rounded-full bg-accent-500 inline-block"></span>
                读者口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  {
                    name: '漫画迷小王',
                    text: '073漫画网真的是我用过最好的漫画阅读网站！下拉式阅读太方便了，不用一页一页地点，看洞洞杂货铺一口气看了几十话，超级过瘾！',
                    rating: 5,
                  },
                  {
                    name: '追漫少女',
                    text: '每天必刷073漫画网，更新速度快，画质清晰，而且完全免费！手机上看也很流畅，等公交的时候刷两话美滋滋。',
                    rating: 5,
                  },
                  {
                    name: '二次元老司机',
                    text: '作为一个看了十几年漫画的老读者，073漫画网的阅读体验是真的好。页面简洁没广告骚扰，分类也很全，推荐给所有漫友！',
                    rating: 5,
                  },
                ].map((item) => (
                  <div
                    key={item.name}
                    className="bg-background-100 rounded-xl p-6"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: item.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      {item.text}
                    </p>
                    <p className="text-xs text-foreground-500 font-medium">
                      — {item.name}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact Section */}
            <section className="mb-14" id="contact">
              <h2 className="font-heading text-xl text-foreground-900 mb-6 flex items-center gap-3">
                <span className="w-1 h-6 rounded-full bg-secondary-500 inline-block"></span>
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Contact Info */}
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-foreground-900">电子邮箱</h3>
                      <p className="text-sm text-foreground-500">contact@073mh.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 shrink-0">
                      <i className="ri-customer-service-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-foreground-900">在线客服</h3>
                      <p className="text-sm text-foreground-500">工作日 9:00 - 18:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-foreground-900">公司地址</h3>
                      <p className="text-sm text-foreground-500">中国·湖南省</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 shrink-0">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-foreground-900">微信公众号</h3>
                      <p className="text-sm text-foreground-500">搜索关注「073漫画网」</p>
                    </div>
                  </div>
                </div>

                {/* Contact Form */}
                <div className="bg-background-100 rounded-xl p-6">
                  <h3 className="font-heading text-base text-foreground-900 mb-4">发送消息</h3>
                  <form onSubmit={handleSubmit} data-readdy-form="">
                    {/* Honeypot */}
                    <div className="hp-field-wrap">
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm text-foreground-600 mb-1.5">
                          姓名
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          value={contactForm.name}
                          onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm text-foreground-600 mb-1.5">
                          邮箱
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm text-foreground-600 mb-1.5">
                          留言内容
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          rows={4}
                          maxLength={500}
                          value={contactForm.message}
                          onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-colors resize-none"
                          placeholder="请输入您的留言内容（最多500字）"
                        />
                        <p className="text-xs text-foreground-400 mt-1 text-right">
                          {contactForm.message.length}/500
                        </p>
                      </div>

                      {formStatus === 'success' ? (
                        <div className="text-center py-3 rounded-lg bg-accent-100 text-accent-700 text-sm">
                          消息已发送成功！我们会尽快回复您。
                        </div>
                      ) : formStatus === 'error' ? (
                        <div className="text-center py-3 rounded-lg bg-primary-100 text-primary-700 text-sm">
                          {formError || '发送失败，请稍后重试。'}
                        </div>
                      ) : null}

                      <button
                        type="submit"
                        className="w-full py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        发送消息
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </section>

            {/* FAQ Section */}
            <section className="pb-14">
              <h2 className="font-heading text-xl text-foreground-900 mb-6 flex items-center gap-3">
                <span className="w-1 h-6 rounded-full bg-primary-500 inline-block"></span>
                常见问题
              </h2>
              <div className="space-y-3">
                {[
                  {
                    q: '073漫画网的漫画是否全部免费？',
                    a: '是的，073漫画网上所有漫画均完全免费阅读，无需付费订阅，也无需注册账号即可畅读。',
                  },
                  {
                    q: '如何在073漫画网阅读漫画？',
                    a: '073漫画网采用下拉式阅读方式，操作简单便捷。打开任意漫画章节，向下滑动即可连续阅读，无需翻页。',
                  },
                  {
                    q: '073漫画网的漫画多久更新一次？',
                    a: '洞洞杂货铺等热门漫画每日更新，其他漫画根据作者发布频率同步更新，确保读者第一时间看到最新内容。',
                  },
                  {
                    q: '可以用手机阅读073漫画网的漫画吗？',
                    a: '可以的！073漫画网完美适配手机端，无需下载APP，打开手机浏览器即可享受流畅的下拉式漫画阅读体验。',
                  },
                ].map((faq, index) => (
                  <details
                    key={index}
                    className="bg-background-100 rounded-xl overflow-hidden group"
                  >
                    <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                      <span className="text-sm font-medium text-foreground-800 pr-4">
                        {faq.q}
                      </span>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}